import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import styled from "styled-components";
import * as MatStyled from "@mui/material/styles";
import {
  TextField,
  Grid,
  Typography,
  IconButton,
  Card,
  CardContent,
  FormControl,
} from "@mui/material";

import CloseIcon from "@mui/icons-material/Close";
import PhotoOutlinedIcon from "@mui/icons-material/PhotoOutlined";

import Button from "src/components/core/Button";
import UploadImage from "src/components/upload/dropzone/UploadImage";
import Loading from "src/components/ui/Loading";
import device from "src/styles/device";
import { useSnackbar } from "notistack";
import { useLoading } from "src/context/LoadingContext";
import {
  createCertificate,
  updateCertificate,
  getCertificateDetail,
} from "src/api/certificate";

// Styled Components
const Main = MatStyled.styled("main", {
  shouldForwardProp: (prop) => prop !== "open",
})(({ theme, open }) => ({
  width: "100%",
  flexGrow: 1,
  padding: theme.spacing(3),
  transition: theme.transitions.create("margin", {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen,
  }),
  ...(open && {
    transition: theme.transitions.create("margin", {
      easing: theme.transitions.easing.easeOut,
      duration: theme.transitions.duration.enteringScreen,
    }),
    marginLeft: 0,
  }),
  [theme.breakpoints.down("md")]: {
    padding: theme.spacing(2),
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const DrawerHeader = MatStyled.styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(0, 1),
  ...theme.mixins.toolbar,
  justifyContent: "flex-end",
}));

const Container = styled.div`
  width: 100%;
  max-width: 1240px;
  position: relative;
  padding: 32px;
  margin: 0 auto;

  @media ${device.tabletL} {
    padding: 24px 20px;
  }

  @media ${device.mobileL} {
    padding: 16px;
  }
`;

const Header = styled.div`
  font-size: 1.5rem;
  font-style: normal;
  font-weight: 600;
  display: inline-block;
  width: 100%;
  text-align: center;

  @media ${device.tabletL} {
    font-size: 1rem;
  }
`;

const FormCard = styled(Card)`
  border-radius: 16px;
  box-shadow: none !important;

  @media ${device.tabletL} {
    border-radius: 12px;
    margin-bottom: 24px;
  }

  @media ${device.mobileL} {
    margin-bottom: 20px;
  }
`;

const UploadBox = styled.div`
  border: 2px dashed #d0d0d4;
  border-radius: 16px;
  padding: 48px 24px;
  text-align: center;
  background: #fbfbfb;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  width: 200px;
  height: 200px;

  &:hover {
    border-color: #e04132;
    background: #f9f9f9;
  }

  @media ${device.tabletL} {
    padding: 40px 20px;
    min-height: 200px;
  }

  @media ${device.mobileL} {
    padding: 32px 16px;
    min-height: 180px;
  }
`;

const UploadedPreview = styled.div`
  position: relative;
  border-radius: 16px;
  overflow: hidden;
  background: #f5f5f5;
  border: 1px solid #e0e0e0;
  width: 200px;
  height: 200px;
`;

const PreviewImage = styled.img`
  width: 100%;
  height: 200px;
  object-fit: cover;
  display: block;

  @media ${device.mobileL} {
    height: 160px;
  }
`;

const RemoveButton = styled(IconButton)`
  position: absolute !important;
  top: 10px;
  right: 10px;
  background: rgb(255, 255, 255) !important;
  color: #e04132;
  width: 28px;
  height: 28px;
  box-shadow: 0px 2px 6px rgba(0, 0, 0, 0.2);

  &:hover {
    background: rgba(255, 255, 255, 1);
    color: #c73529;
  }
`;

const UploadText = styled(Typography)`
  color: #666666;
  margin-top: 16px;
  text-align: center;
  font-weight: 500;
  font-size: 0.875rem !important;

  @media ${device.mobileL} {
    // font-size: 0.875rem;
    margin-top: 12px;
  }
`;

const UploadHelper = styled(Typography)`
  color: #999999;
  font-size: 0.875rem;
  margin-top: 8px;
  text-align: center;
  line-height: 1.4;

  @media ${device.mobileL} {
    font-size: 0.75rem;
  }
`;

const Panel = styled.div`
  border-radius: 24px;
  // border: 1px solid var(--Grey-stroke, #d8d8dc);
  background: var(--White, #fff);
  display: flex;
  margin: 24px auto 0 auto;
  padding: 30px 24px 40px 24px;
  width: 100%;
  max-width: 1152px;
  box-shadow: 1px 4px 16px rgba(0, 0, 0, 0.08);

  @media ${device.tabletL} {
    padding: 0px 40px;
    border: none;
  }
  @media ${device.mobileL} {
    padding: 0 24px;
  }
`;

const Row = styled.div`
  display: flex;
  width: 100%;
  flex-direction: ${({ direction }) => direction || "column"};
  margin: ${({ margin }) => margin};
  justify-content: ${({ justifyContent }) => justifyContent || "flex-start"};
`;

const Topic = styled.span`
  color: ${({ condition, required }) =>
    condition ? "#6C6C70" : required ? "#E13B30" : "#252525"};
  font-family: Prompt;
  font-size: 1.25rem;
  font-style: normal;
  font-weight: 400;
  display: block;
  display: flex;
  margin-left: ${({ condition }) => condition && "10px"};

  @media ${device.tabletL} {
    font-size: 1rem;
  }
`;

const CertificateForm = ({ open }) => {
  const { certificateId } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [isLoading, setIsLoading] = useState(false);
  const [uploadedImage, setUploadedImage] = useState(null);

  const { setLoading } = useLoading();

  const initialCertificateInfo = {
    name: "",
    description: "",
    image: null,
  };
  const [certificateInfo, setCertificateInfo] = useState(
    initialCertificateInfo,
  );
  useEffect(() => {
    if (certificateId) {
      // Fetch existing certificate data for editing
      const fetchCertificate = async () => {
        try {
          setIsLoading(true);
          setLoading(true);
          // Simulate API call to fetch certificate details
          // Replace this with actual API call
          const response = await getCertificateDetail(certificateId);

          setCertificateInfo({
            name: response.name,
            description: response.description,
            icon: response.icon,
          });

          if (response.icon) {
            // If there's an existing image, you might want to fetch and set it
            // Here we just simulate that the image is already uploaded
            setUploadedImage(response.icon);
          }
        } catch (error) {
          console.error("Error fetching certificate details:", error);
          enqueueSnackbar("เกิดข้อผิดพลาดในการโหลดข้อมูลใบรับรอง", {
            variant: "error",
          });
        } finally {
          setIsLoading(false);
          setLoading(false);
        }
      };

      fetchCertificate();
    }
  }, [certificateId, enqueueSnackbar, setLoading]);
  // const handleBack = () => {
  //   navigate("/certificate/list");
  // };

  const onSubmit = async (data) => {
    try {
      setIsLoading(true);

      // Validate files
      if (!uploadedImage) {
        enqueueSnackbar("กรุณาอัปโหลดรูปภาพใบรับรอง", { variant: "error" });
        setIsLoading(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("description", data.description);
      formData.append("icon", uploadedImage);

      await createCertificate(formData);

      enqueueSnackbar("สร้างใบรับรองสำเร็จ", { variant: "success" });
      navigate("/certificate/list");
    } catch (error) {
      console.error("Error creating certificate:", error);

      // Show specific error message if available
      const errorMessage =
        error?.response?.data?.message || "เกิดข้อผิดพลาดในการสร้างใบรับรอง";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };
  const onUpdate = async (data) => {
    try {
      setIsLoading(true);

      // Validate files
      if (!uploadedImage) {
        enqueueSnackbar("กรุณาอัปโหลดรูปภาพใบรับรอง", { variant: "error" });
        setIsLoading(false);
        return;
      }

      // Create FormData for file upload
      const formData = new FormData();
      formData.append("name", data.name);
      formData.append("description", data.description);
      formData.append("icon", uploadedImage);

      await updateCertificate(certificateId, formData);

      enqueueSnackbar("แก้ไขใบรับรองสำเร็จ", { variant: "success" });
      navigate("/certificate/list");
    } catch (error) {
      console.error("Error updating certificate:", error);

      // Show specific error message if available
      const errorMessage =
        error?.response?.data?.message || "เกิดข้อผิดพลาดในการแก้ไขใบรับรอง";
      enqueueSnackbar(errorMessage, { variant: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleImageUpload = (file) => {
    if (file) {
      setUploadedImage(file);
      setCertificateInfo({ ...certificateInfo, icon: file });
    }
  };

  const removeImage = () => {
    setUploadedImage(null);
    setCertificateInfo({ ...certificateInfo, icon: null });
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <Main open={open}>
      <DrawerHeader />

      <Container>
        {/* Header */}
        <Header>
          {!certificateId ? "สร้างใบรับรองใหม่" : "แก้ไขใบรับรอง"}
        </Header>

        <Panel>
          <Grid container ml={0} columnSpacing={4}>
            <FormControl fullWidth>
              <Grid xs={12} mb={6}>
                <Row margin="0 0 16px 0">
                  <Topic>ข้อมูลใบรับรอง</Topic>
                </Row>

                <Grid container xs={12} columnSpacing={6}>
                  <Grid item xs={6}>
                    <TextField
                      required
                      fullWidth
                      size="medium"
                      label="ชื่อใบรับรอง"
                      placeholder="กรุณากรอกชื่อใบรับรอง"
                      value={certificateInfo.name}
                      onChange={({ target }) =>
                        setCertificateInfo({
                          ...certificateInfo,
                          name: target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>

                <Grid container xs={12} mt={2} columnSpacing={6}>
                  <Grid item xs={6}>
                    <TextField
                      fullWidth
                      required
                      label="รายละเอียด"
                      placeholder="อธิบายรายละเอียดเกี่ยวกับใบรับรองนี้ เช่น วัตถุประสงค์ ขอบเขต และประโยชน์ของใบรับรอง"
                      multiline
                      rows={4}
                      value={certificateInfo.description}
                      onChange={({ target }) =>
                        setCertificateInfo({
                          ...certificateInfo,
                          description: target.value,
                        })
                      }
                    />
                  </Grid>
                </Grid>
              </Grid>

              <Grid container xs={12} columnSpacing={6}>
                <Grid item xs={6}>
                  <Row margin="0 0 16px 0">
                    <Topic variant="h6">รูปภาพใบรับรอง *</Topic>
                  </Row>

                  <FormCard>
                    <Grid container xs={12} sx={{ margin: "auto" }}>
                      <CardContent sx={{ pb: "16px !important" }}>
                        {!uploadedImage ? (
                          <UploadImage
                            callBackFile={handleImageUpload}
                            sizeFile={4194304} // 4MB
                            closeAlert={false}
                          >
                            <UploadBox>
                              <PhotoOutlinedIcon
                                sx={{
                                  fontSize: 45,
                                  color: "#999999",
                                  marginBottom: "8px",
                                }}
                              />
                              <UploadText>อัปโหลดรูปภาพใบรับรอง</UploadText>
                              <UploadHelper variant="caption">
                                รองรับไฟล์ JPG, PNG ขนาดไม่เกิน 4MB
                              </UploadHelper>
                            </UploadBox>
                          </UploadImage>
                        ) : (
                          <UploadedPreview>
                            <PreviewImage
                              src={
                                uploadedImage && typeof uploadedImage === "string" &&
                                uploadedImage?.startsWith("http")
                                  ? uploadedImage
                                  : URL.createObjectURL(uploadedImage)
                              }
                              alt="Certificate preview"
                            />
                            <RemoveButton onClick={removeImage}>
                              <CloseIcon sx={{ fontSize: 22 }} />
                            </RemoveButton>
                          </UploadedPreview>
                        )}
                      </CardContent>
                    </Grid>
                  </FormCard>
                </Grid>
              </Grid>
            </FormControl>
          </Grid>
        </Panel>

        <Row direction="row" margin="48px 0 0 0" justifyContent="center">
          {certificateId ? (
            <>
              <Button
                variant="outlined"
                size="large"
                // disabled={!isSaveDraftValid}
                onClick={() => {}}
                sx={{
                  width: "100%",
                  maxWidth: 160,
                  borderRadius: 4,
                  mx: 2,
                }}
              >
                ยกเลิก
              </Button>

              <Button
                variant="contained"
                size="large"
                disabled={
                  !certificateInfo.name ||
                  !certificateInfo.description ||
                  !certificateInfo.icon
                }
                onClick={() => onUpdate(certificateInfo)}
                sx={{ width: "100%", maxWidth: 160, borderRadius: 4, mx: 2 }}
              >
                แก้ไขใบรับรอง
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              size="large"
              disabled={
                !certificateInfo.name ||
                !certificateInfo.description ||
                !certificateInfo.icon
              }
              onClick={() => onSubmit(certificateInfo)}
              sx={{ width: "100%", maxWidth: 160, borderRadius: 4, mx: 2 }}
            >
              สร้างใบรับรอง
            </Button>
          )}
        </Row>
      </Container>
    </Main>
  );
};

export default CertificateForm;
